import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';
import { parseAddress } from '@featit/shared/addressing/paths';
import { useStores } from '@/hooks/useStores';
import { lazyOverlay } from './lazyRoute';
import { workAreaRouteState } from './workAreaRouteState';
import { AddressState, NamespaceWorkAreas } from './WorkAreaRouteStates';

/** F-0788 (PJ-10c): a namespace's Public work areas, for a reader who holds none in it. */
const NamespacePublicWorkAreas = lazyOverlay(
  () => import('@/features/examples/NamespacePublicWorkAreas'),
  (module) => module.NamespacePublicWorkAreas,
);

/** F-0788 (PJ-10d): below a reader's own work areas, the namespace's Public ones they are not in. */
const NamespaceOtherPublicWorkAreas = lazyOverlay(
  () => import('@/features/examples/NamespacePublicWorkAreas'),
  (module) => module.NamespaceOtherPublicWorkAreas,
);

/**
 * `/acme`: a namespace, and the work areas in it the reader can open (R-5, F-0250, AC-AD12).
 *
 * ⭐ The first level of an address is always the handle, so a single segment is a namespace — never
 * the feedback entry it was before the cutover, when `/:id` was the router's catch-all. An old
 * feedback link, `/<uuid>`, is still honoured: when no namespace answers and the segment has the
 * shape of a feedback id, it goes to `/-/feedback/<uuid>`. A real namespace always wins.
 *
 * An old app address with nothing after it (`/settings`) arrives here too, and is replaced by its new
 * one (AC-AD6).
 *
 * Which of these it is, is `workAreaRouteState`'s to decide, so the header, the tab and the switcher
 * name the state this draws (I-0793).
 *
 * F-0788 (PJ-10c): where the reader holds no work area in the namespace, its bare address asks for
 * the namespace's Public work areas first, and answers as before only where it lists none. PJ-10d:
 * where they hold some, those Public ones they are not in follow their own.
 */
export const NamespaceAddressPage = observer(function NamespaceAddressPage() {
  const location = useLocation();
  const { projectStore } = useStores();
  const state = workAreaRouteState({
    pathname: location.pathname,
    matchedRef: undefined,
    boundary: false,
    projects: projectStore.projects,
    answered: projectStore.projects.length > 0 || projectStore.hasWorkAreaAnswer,
  });
  // A single segment is never a page of its own: every state is drawn by `AddressState`, the
  // namespace's own list and its Public work areas (F-0788) included.
  if (state.kind === 'page') return null;
  const address = parseAddress(location.pathname);
  if (state.kind === 'no-page' && address.kind === 'namespace' && address.route === '') {
    return (
      <NamespacePublicWorkAreas
        namespace={address.namespace}
        fallback={<AddressState state={state} />}
      />
    );
  }
  if (state.kind === 'namespace' && address.kind === 'namespace' && address.route === '') {
    return (
      <NamespaceWorkAreas
        namespace={state.namespace}
        workAreas={state.workAreas}
        after={
          <NamespaceOtherPublicWorkAreas
            namespace={address.namespace}
            heldIds={state.workAreas.map((workArea) => workArea.id)}
          />
        }
      />
    );
  }
  return <AddressState state={state} />;
});
